<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Text, CardGrid, SelectCard, SliderV2, Radio } from '@Shelly/shelly-design-system';
	import { capFirstChar } from '../../../../../../../../../helpers/utilities';
	import { ventoraClamp } from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		VENTORA_FAN_RUN_MODES,
		VENTORA_FAN_SCENE_UI,
		type VentoraFanRunMode,
	} from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';

	export let actions = [];
	export let active_actions = [];
	export let sceneIsValid = false;

	const dispatch = createEventDispatcher();

	const FAN_MODE_ACTION_ID = VENTORA_FAN_SCENE_UI.MODE;
	const FAN_ON_ACTION_ID = VENTORA_FAN_SCENE_UI.ON;
	const FAN_SPEED_ACTION_ID = VENTORA_FAN_SCENE_UI.SPEED;

	const fanOnAction = actions.find((action) => action.id === FAN_ON_ACTION_ID);
	const fanSpeedAction = actions.find((action) => action.id === FAN_SPEED_ACTION_ID);

	let runModeValue: VentoraFanRunMode = 'manual';
	let turnValue = 'on';
	let speedValue = 50;
	let initialized = false;

	const getActiveActionValue = (actionName: string) =>
		active_actions.find((action) => action.name === actionName)?.value;

	const syncFromActiveActions = () => {
		const modeValue = getActiveActionValue(FAN_MODE_ACTION_ID);
		const onValue = getActiveActionValue(FAN_ON_ACTION_ID);
		const speedActionValue = getActiveActionValue(FAN_SPEED_ACTION_ID);

		if (modeValue === 'auto' || modeValue === 'manual') {
			runModeValue = modeValue;
		}

		if (onValue === 'on' || onValue === 'off') {
			turnValue = onValue;
		}

		if (speedActionValue !== undefined && speedActionValue !== '') {
			speedValue = ventoraClamp(Number(speedActionValue), 1, 100);
		}
	};

	const emitActiveActions = () => {
		active_actions = [
			{ name: FAN_MODE_ACTION_ID, value: runModeValue },
			{ name: FAN_ON_ACTION_ID, value: turnValue },
			{ name: FAN_SPEED_ACTION_ID, value: speedValue },
		];
		sceneIsValid = true;
		dispatch('change_active', { new_active: [...active_actions] });
	};

	$: if (!initialized) {
		if (active_actions?.length > 0) {
			syncFromActiveActions();
		}
		emitActiveActions();
		initialized = true;
	}

	const onSelectRunMode = (value: VentoraFanRunMode) => {
		runModeValue = value;
		emitActiveActions();
	};

	const onSelectFanOn = (value: string) => {
		turnValue = value;
		emitActiveActions();
	};

	const onSpeedChange = (event: CustomEvent<{ value: number }>) => {
		speedValue = ventoraClamp(event.detail.value, 1, 100);
		emitActiveActions();
	};

	$: sliderA11y = {
		sliderAriaLabel: $format('label_fan_speed', { default: 'Fan speed' }),
		ariaLabel: $format('label_fan_speed', { default: 'Fan speed' }),
		ariaLabelValue: $format('label_fan_speed', { default: 'Fan speed' }),
	};
</script>

<Text element="p" class="mb-s text-secondary">
	{capFirstChar($format('sub_settings_title_ventora_run_mode', { default: 'Fan run mode' }))}
</Text>

<div class="d-flex flex-direction-column gap-s mb-m">
	{#each VENTORA_FAN_RUN_MODES as runMode}
		<Radio
			size="m"
			selected={runModeValue}
			value={runMode}
			on:click={() => onSelectRunMode(runMode)}
			id={`scene-fan-run-mode-${runMode}`}
		>
			{$format(runMode === 'auto' ? 'ventora_run_mode_auto' : 'ventora_run_mode_manual', {
				default: runMode === 'auto' ? 'Auto' : 'Manual',
			})}
		</Radio>
	{/each}
</div>

{#if runModeValue === 'manual'}
	{#if fanOnAction}
		<Text element="p" class="mb-s text-secondary">
			{capFirstChar($format(fanOnAction.title))}
		</Text>
		<CardGrid sm={200} md={200} xl={200} noPaddingBottom class="mb-m">
			{#each fanOnAction.values as turnOption, index}
				<div class="d-flex">
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						class="width-full"
						type="horizontal"
						variant="radio"
						title={$format('scene_action_turn_state', {
							values: { turn_state: $format(`label_action_turn_${turnOption}`) },
						})}
						icon={index === 0
							? 'fas fa-power-off text-success'
							: 'fas fa-power-off text-danger'}
						selected={turnValue === turnOption}
						on:click={() => onSelectFanOn(turnOption)}
					/>
				</div>
			{/each}
		</CardGrid>
	{/if}

	{#if fanSpeedAction}
		<SliderV2
			id="fan-speed-slider"
			class="mb-m"
			type="simple"
			value={speedValue}
			min={1}
			max={100}
			step={1}
			unit="%"
			title={$format('ventora_manual_mode_speed', {
				values: { speed: speedValue },
				default: 'Speed {speed}%',
			})}
			a11yProps={sliderA11y}
			on:value_change={onSpeedChange}
		/>
	{/if}
{/if}
